const MAX_BYTES = 1_048_576;

const ACCEPTED_TYPES = new Set(['image/png', 'image/jpeg']);

const MIN_DIM = 512;

const JPEG_QUALITIES = [0.8, 0.7, 0.6, 0.5, 0.4, 0.3, 0.2];

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Falha ao carregar imagem para compressão'));
    img.src = src;
  });
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  type: string,
  fileName: string,
  quality?: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Falha ao codificar a imagem'));
          return;
        }
        resolve(new File([blob], fileName, { type }));
      },
      type,
      type === 'image/jpeg' ? quality ?? 0.8 : undefined,
    );
  });
}

function renderCanvas(
  img: HTMLImageElement,
  w: number,
  h: number,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

function dimsAt(
  targetShort: number,
  origW: number,
  origH: number,
  minDim: number,
): { w: number; h: number } {
  const shorterOriginal = Math.min(origW, origH);
  const ratio = targetShort / shorterOriginal;
  return {
    w: Math.max(minDim, Math.round(origW * ratio)),
    h: Math.max(minDim, Math.round(origH * ratio)),
  };
}

async function tryCompressJpeg(
  img: HTMLImageElement,
  fileName: string,
): Promise<File> {
  const origW = img.naturalWidth;
  const origH = img.naturalHeight;
  const shorterOriginal = Math.min(origW, origH);
  const jpegName = fileName.replace(/\.png$/i, '.jpg');

  // Imagem já menor que 512px no lado menor: mantém dimensões originais e
  // tenta apenas baixar a qualidade até caber em 1 MB.
  if (shorterOriginal < MIN_DIM) {
    const canvas = renderCanvas(img, origW, origH);
    for (const q of JPEG_QUALITIES) {
      const file = await canvasToFile(canvas, 'image/jpeg', jpegName, q);
      if (file.size <= MAX_BYTES) return file;
    }
    // Caso irredutível: mesmo à qualidade mínima (0.2) mantém>1MB nas dimensões
    // originais. Devolve o melhor possível (qualidade mínima) — nunca o original.
    return canvasToFile(canvas, 'image/jpeg', jpegName, 0.2);
  }

  let low = MIN_DIM;
  let high = shorterOriginal;
  let best: File | null = null;

  while (low <= high) {
    const mid = Math.round((low + high) / 2);
    const { w, h } = dimsAt(mid, origW, origH, MIN_DIM);
    const canvas = renderCanvas(img, w, h);
    let fits = false;

    for (const q of JPEG_QUALITIES) {
      const file = await canvasToFile(canvas, 'image/jpeg', jpegName, q);
      if (file.size <= MAX_BYTES) {
        best = file;
        low = mid + 1;
        fits = true;
        break;
      }
    }

    if (!fits) {
      high = mid - 1;
    }
  }

  if (best) return best;

  // Caso irredutível: mesmo a 512px com qualidade mínima o JPEG>1MB.
  // Devolve o melhor possível dentro das restrições — nunca o original.
  const { w: mw, h: mh } = dimsAt(MIN_DIM, origW, origH, MIN_DIM);
  const minCanvas = renderCanvas(img, mw, mh);
  return canvasToFile(minCanvas, 'image/jpeg', jpegName, 0.2);
}

async function tryCompressPng(
  img: HTMLImageElement,
  fileName: string,
): Promise<File> {
  const origW = img.naturalWidth;
  const origH = img.naturalHeight;
  const shorterOriginal = Math.min(origW, origH);

  // Imagem já menor que 512px no lado menor: mantém dimensões originais (não
  // pode reduzir mais sem violar o mínimo). Se ainda > 1MB, converte a JPEG.
  if (shorterOriginal < MIN_DIM) {
    const canvas = renderCanvas(img, origW, origH);
    const png = await canvasToFile(canvas, 'image/png', fileName);
    if (png.size <= MAX_BYTES) return png;

    // PNG pequeno mas denso: converte a JPEG como último recurso
    // (abandona transparência quando estritamente necessário).
    return tryCompressJpeg(img, fileName);
  }

  let low = MIN_DIM;
  let high = shorterOriginal;
  let best: File | null = null;

  while (low <= high) {
    const mid = Math.round((low + high) / 2);
    const { w, h } = dimsAt(mid, origW, origH, MIN_DIM);
    const canvas = renderCanvas(img, w, h);
    const file = await canvasToFile(canvas, 'image/png', fileName);

    if (file.size <= MAX_BYTES) {
      best = file;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  if (best) return best;

  // Caso irredutível em PNG: mesmo a 512px o PNG continua > 1MB. Converte a
  // JPEG como último recurso (abandona transparência quando estritamente
  // necessário) para cumprir o limite.
  return tryCompressJpeg(img, fileName);
}

export async function compressImageIfNeeded(
  file: File,
): Promise<File> {
  if (file.size <= MAX_BYTES) return file;
  if (!ACCEPTED_TYPES.has(file.type)) return file;

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao ler ficheiro para compressão'));
    reader.readAsDataURL(file);
  });

  const img = await loadImage(dataUrl);

  return file.type === 'image/jpeg'
    ? tryCompressJpeg(img, file.name)
    : tryCompressPng(img, file.name);
}
